import { useEffect, useState, type FormEvent } from 'react';
import { FormCheckbox, FormField, FormSelect } from '../shared/form';
import { useCreateModel, useModels } from '../../hooks/useModels';
import type { CreateModelInput, ModelProvider, ModelStatus } from '../../types';
import { CatalogDialog, PRIMARY_BUTTON, SECONDARY_BUTTON } from './CatalogDialog';
import { catalogFormErrors, type CatalogFormErrors } from './catalogErrors';
import { SuccessorSelect } from './SuccessorSelect';

/**
 * Add model (task 1102): a Model row entered by hand, for a provider the
 * catalog review's models.dev source does not cover. `POST /api/models`
 * (service task 1101), sent with the Model row's own field names. The
 * optional Successor (task 1130) is the Model the resolver falls back to once
 * this one is withdrawn.
 */

/**
 * The statuses a hand-entered Model can start in. Task 1102 names the third
 * one "hidden"; the Model row's status is active | deprecated | retired, and
 * `retired` is the one hidden from pickers and from `GET /api/models`.
 */
export const ADD_MODEL_STATUS_OPTIONS: { value: ModelStatus; label: string }[] = [
  { value: 'active', label: 'active' },
  { value: 'deprecated', label: 'deprecated' },
  { value: 'retired', label: 'retired' },
];

const EMPTY_ERRORS: CatalogFormErrors = { fields: {}, general: null };

interface AddModelDialogProps {
  open: boolean;
  onClose: () => void;
  providers: ModelProvider[];
}

const isPositiveInteger = (value: string) => /^\d+$/.test(value.trim()) && Number(value) > 0;
const isPrice = (value: string) => value.trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;

export function AddModelDialog({ open, onClose, providers }: AddModelDialogProps) {
  const create = useCreateModel();
  // Non-retired catalog Models (the list's default), the Successor choices.
  const { data: catalog } = useModels();
  const enabledProviders = providers.filter((provider) => provider.enabled);
  const [providerId, setProviderId] = useState('');
  const [modelId, setModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [contextWindow, setContextWindow] = useState('');
  const [maxOutput, setMaxOutput] = useState('');
  const [inputPrice, setInputPrice] = useState('');
  const [outputPrice, setOutputPrice] = useState('');
  const [status, setStatus] = useState<ModelStatus>('active');
  const [supportsTemperature, setSupportsTemperature] = useState(true);
  const [successorModelId, setSuccessorModelId] = useState('');
  const [errors, setErrors] = useState<CatalogFormErrors>(EMPTY_ERRORS);

  useEffect(() => {
    if (!open) return;
    setProviderId('');
    setModelId('');
    setDisplayName('');
    setContextWindow('');
    setMaxOutput('');
    setInputPrice('');
    setOutputPrice('');
    setStatus('active');
    setSupportsTemperature(true);
    setSuccessorModelId('');
    setErrors(EMPTY_ERRORS);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validate = (): Record<string, string> => {
    const fields: Record<string, string> = {};
    if (!providerId) fields.providerId = 'Choose a provider';
    if (!modelId.trim()) fields.modelId = 'Model ID is required';
    if (!displayName.trim()) fields.displayName = 'Display name is required';
    if (!isPositiveInteger(contextWindow)) fields.contextWindow = 'Enter a whole number of tokens';
    if (!isPositiveInteger(maxOutput)) fields.maxOutputTokens = 'Enter a whole number of tokens';
    if (inputPrice.trim() && !isPrice(inputPrice)) fields.inputPriceMTok = 'Enter a price of 0 or more';
    if (outputPrice.trim() && !isPrice(outputPrice)) fields.outputPriceMTok = 'Enter a price of 0 or more';
    return fields;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const fields = validate();
    if (Object.keys(fields).length > 0) {
      setErrors({ fields, general: null });
      return;
    }
    setErrors(EMPTY_ERRORS);
    const payload: CreateModelInput = {
      providerId,
      modelId: modelId.trim(),
      displayName: displayName.trim(),
      contextWindow: Number(contextWindow),
      maxOutputTokens: Number(maxOutput),
      ...(inputPrice.trim() ? { inputPriceMTok: Number(inputPrice) } : {}),
      ...(outputPrice.trim() ? { outputPriceMTok: Number(outputPrice) } : {}),
      status,
      supportsTemperature,
      ...(successorModelId ? { successorModelId } : {}),
    };
    try {
      await create.mutateAsync(payload);
      onClose();
    } catch (error) {
      setErrors(catalogFormErrors(error, 'modelId', { successorField: 'successorModelId' }));
    }
  };

  return (
    <CatalogDialog
      open={open}
      onClose={onClose}
      busy={create.isPending}
      title="Add model"
      description="A Model entered by hand, for a provider no catalog source lists."
      testId="add-model-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormSelect
          label="Provider"
          name="model-provider"
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
          options={enabledProviders.map((provider) => ({ value: provider.id, label: provider.displayName }))}
          placeholder="Select a provider"
          error={errors.fields.providerId}
          required
        />
        <div>
          <FormField
            label="Model ID"
            name="model-model-id"
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            error={errors.fields.modelId}
            placeholder="e.g. llama-3.3-70b-versatile"
            required
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">The identifier sent to the provider.</p>
        </div>
        <FormField
          label="Display name"
          name="model-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={errors.fields.displayName}
          required
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Context window (tokens)"
            name="model-context-window"
            type="number"
            min={1}
            step={1}
            value={contextWindow}
            onChange={(e) => setContextWindow(e.target.value)}
            error={errors.fields.contextWindow}
            required
          />
          <FormField
            label="Max output (tokens)"
            name="model-max-output"
            type="number"
            min={1}
            step={1}
            value={maxOutput}
            onChange={(e) => setMaxOutput(e.target.value)}
            error={errors.fields.maxOutputTokens}
            required
          />
          <FormField
            label="Input price per 1M tokens"
            name="model-input-price"
            type="number"
            min={0}
            step="any"
            value={inputPrice}
            onChange={(e) => setInputPrice(e.target.value)}
            error={errors.fields.inputPriceMTok}
          />
          <FormField
            label="Output price per 1M tokens"
            name="model-output-price"
            type="number"
            min={0}
            step="any"
            value={outputPrice}
            onChange={(e) => setOutputPrice(e.target.value)}
            error={errors.fields.outputPriceMTok}
          />
        </div>
        <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">A price left blank is stored as $0.00.</p>
        <FormSelect
          label="Status"
          name="model-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as ModelStatus)}
          options={ADD_MODEL_STATUS_OPTIONS}
          error={errors.fields.status}
        />
        <FormCheckbox
          label="Accepts temperature"
          name="model-supports-temperature"
          checked={supportsTemperature}
          onChange={(e) => setSupportsTemperature(e.target.checked)}
          description="Clear this for a model whose provider rejects the temperature parameter; calls on it leave temperature out."
          error={errors.fields.supportsTemperature}
        />
        <SuccessorSelect
          name="model-successor"
          value={successorModelId}
          onChange={setSuccessorModelId}
          models={catalog ?? []}
          error={errors.fields.successorModelId}
        />

        {errors.general && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {errors.general}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} disabled={create.isPending} className={SECONDARY_BUTTON}>
            Cancel
          </button>
          <button type="submit" disabled={create.isPending} className={PRIMARY_BUTTON}>
            {create.isPending ? 'Adding…' : 'Add model'}
          </button>
        </div>
      </form>
    </CatalogDialog>
  );
}
